<script>
  import { createEventDispatcher } from 'svelte';
  import settings from '../config';
  import session from '../lib/session';
  import { t9n } from '../lib/translate';
  import { TS } from '../config/translation-keys';
  import CheckBox from '../lib/components/CheckBox.svelte';
  import NumberFieldWithButtons from '../lib/components/NumberFieldWithButtons.svelte';
  import InputWithValidation from '../lib/components/InputField.svelte';
  import { show_tutorial_screen } from '../lib/tutorials';

  export let page;
  export let input;
  export let is_address_valid = true;
  export let variant = 'outlined';
  export let page_state;
  export let is_first_time = false;
  export let wait_api;

  const dispatch = createEventDispatcher();

  let address_is_mandatory;
  let has_loaded = false;
  let data;
  let map;
  let marker;
  let geocoder;
  let autocompleteContainer;
  let allow_unlimited_distance;
  let placeAutocomplete;
  let current_pos;
  let invalid_text = "";
  let distance_max;
  let labels = {
    unlimited_distance: null,
    distance: null,
    btn_back: null,
    btn_save: null,
    btn_next: null,
    invalid_str: null,
    invalid_str_name: null,
    invalid_ctry: null,
    invalid_post: null,
    invalid_addr_line: null,
    invalid_cord: null,
    no_res: null,
    geo_fail: null,
    choose_map: null,
    prm_addr: null,
    sec_addr: null,
    title_city: null,
    title_country: null,
    title_postal: null,
    btn_prev: null,
    coord: null,
  };

  let coord_input = "";

  const handleCoordSearch = () => {
    if (!coord_input) return;
    const parts = coord_input.split(/[\s,]+/).map(p => parseFloat(p.trim()));
    if (parts.length >= 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
      const newPos = { lat: parts[0], lng: parts[1] };
      if (marker) marker.position = newPos;
      if (map) map.setCenter(newPos);
      validatePosition(newPos);
    }
  };

  const initMap = async () => {
    try {
      // Fontos: Az importLibrary-t használjuk
      const { Map } = await google.maps.importLibrary('maps');
      const { AdvancedMarkerElement } = await google.maps.importLibrary('marker');
      const { Geocoder } = await google.maps.importLibrary('geocoding');
      const { PlaceAutocompleteElement } = await google.maps.importLibrary('places');

      geocoder = new Geocoder();

      const position = {
        lat: current_pos.lat || 0,
        lng: current_pos.lng || 0,
      };

      map = new Map(document.getElementById('map'), {
        center: position,
        zoom: 16,
        mapId: '685d309066497f1b', // Javasolt egy valódi Map ID létrehozása a Cloud Console-ban a DEMO helyett
      });

      marker = new AdvancedMarkerElement({
        map: map,
        position: position,
        gmpDraggable: true,
      });

      placeAutocomplete = new PlaceAutocompleteElement();
      autocompleteContainer.innerHTML = ''; // Tisztítás
      autocompleteContainer.appendChild(placeAutocomplete);

      placeAutocomplete.addEventListener('gmp-select', async (event) => {
        const prediction = event.detail?.placePrediction || event.placePrediction;
        if (!prediction) return;

        try {
          const place = prediction.toPlace();
          await place.fetchFields({ fields: ['location', 'formattedAddress'] });

          if (place.location) {
            marker.position = place.location;
            map.setCenter(place.location);
            map.setZoom(16);
            validatePosition(place.location);
          }
        } catch (error) {
          console.error("Autocomplete error:", error);
          is_address_valid = false;
        }
      });

      marker.addListener('dragend', () => {
        validatePosition(marker.position);
      });

    } catch (e) {
      console.error("Map initialization failed:", e);
    }
  };

  const processGeocodeResult = (result) => {
    let components = {
      street_number: '',
      route: '',
      locality: '',
      country_code: '',
      postal_code: '',
      region: ''
    };

    result.address_components.forEach(comp => {
      const type = comp.types[0];
      if (type === 'street_number') components.street_number = comp.long_name;
      if (type === 'route') components.route = comp.long_name;
      if (type === 'locality' || type === 'postal_town') components.locality = comp.long_name;
      if (type === 'country') components.country_code = comp.short_name;
      if (type === 'postal_code') components.postal_code = comp.long_name;
      if (type === 'administrative_area_level_1') components.region = comp.long_name;
    });

    const merchant_country = data.country.code;
    if (!$session.merchant?.multi_country && components.country_code !== merchant_country) {
      throw new Error(labels.invalid_ctry);
    }

    if (address_is_mandatory) {
      if (!components.route) throw new Error(labels.invalid_str_name);
      if (!components.street_number) throw new Error(labels.invalid_str);
      if (!components.postal_code) throw new Error(labels.invalid_post);
    }

    return components;
  };

  const validatePosition = (pos) => {
    geocoder.geocode({ location: pos }, (results, status) => {
      if (status === "OK" && results[0]) {
        try {
          const result = results[0];
          const businessData = processGeocodeResult(result);

          is_address_valid = true;
          invalid_text = "";

          const lat = typeof result.geometry.location.lat === 'function' ? result.geometry.location.lat() : result.geometry.location.lat;
          const lng = typeof result.geometry.location.lng === 'function' ? result.geometry.location.lng() : result.geometry.location.lng;

          coord_input = `${lat.toFixed(6)}, ${lng.toFixed(6)}`;

          if (placeAutocomplete) placeAutocomplete.value = result.formatted_address;

          input.primary_address = `${businessData.route} ${businessData.street_number}`;
          input.city = businessData.locality;
          input.postal_code = businessData.postal_code;
          input.region = businessData.region;
          input.latitude = lat;
          input.longitude = lng;
          input.country_code = businessData.country_code;

          dispatch('locationchanged', businessData);

        } catch (bizError) {
          is_address_valid = false;
          invalid_text = bizError.message;
        }
      } else {
        is_address_valid = false;
        invalid_text = labels.invalid_input;
      }
    });
  };

  const load_location = () => {
    if (window.google?.maps?.importLibrary) {
      initMap();
      return;
    }

    // Modern aszinkron betöltő – hozzáadva a szükséges library-kat az URL-hez
    (g=>{var h,a,k,p="The Google Maps JavaScript API",c="google",l="importLibrary",q="__ib__",m=document,b=window;b=b[c]||(b[c]={});var d=b.maps||(b.maps={}),r=new Set,e=new URLSearchParams,u=()=>h||(h=new Promise(async(f,n)=>{await (a=m.createElement("script"));e.set("libraries",[...r]+"");for(k in g)e.set(k.replace(/[A-Z]/g,t=>"_"+t[0].toLowerCase()),g[k]);e.set("callback",c+".maps."+q);a.src=`https://maps.googleapis.com/maps/api/js?`+e;d[q]=f;a.onerror=()=>h=n(Error(p+" could not load."));a.nonce=m.querySelector("script[nonce]")?.nonce||"";m.head.append(a)}));d[l]?console.warn(p+" only loads once. Re-trying:",g):d[l]=(f,...n)=>r.add(f)&&u().then(()=>d[l](f,...n))})({
      key: settings.google_maps_api_key,
      v: "weekly",
      libraries: "places,marker,maps,geocoding" // Expliciten kérjük a könyvtárakat
    });

    initMap();
  };

  $: if (!!$session && !has_loaded) {
    address_is_mandatory = $session.merchant.address_is_mandatory;
    distance_max = $session.merchant.distance.distance_max;
    allow_unlimited_distance = $session.merchant.distance.allow_unlimited_distance;

    data = {
      maps_api_key: settings.google_maps_api_key,
      language: $session.merchant.country_code?.toLowerCase() || 'en',
      country: {
        name: $session.merchant.country_name,
        code: $session.merchant.country_code,
      }
    };

    current_pos = { lat: input.latitude || 47.4979, lng: input.longitude || 19.0402 }; // Alapértelmezett Bp ha üres
    load_location();
    has_loaded = true;
  }

  const translate_texts = () => {
    labels.autosave = t9n(TS.NEW_ORDER_AUTO_SAVE);
    labels.invalid_str = t9n(TS.NEW_ORDER_LOC_INVALID_STR);
    labels.invalid_str_name = t9n(TS.NEW_ORDER_LOC_INVALID_STR_NAME);
    labels.invalid_ctry = t9n(TS.NEW_ORDER_LOC_INV_CTRY);
    labels.invalid_post = t9n(TS.NEW_ORDER_LOC_POST);
    labels.invalid_addr_line = t9n(TS.NEW_ORDER_LOC_ADDR_LINE);
    labels.invalid_cord = t9n(TS.NEW_ORDER_LOC_INV_CORD);
    labels.no_res = t9n(TS.NEW_ORDER_LOC_NO_RES);
    labels.geo_fail = t9n(TS.NEW_ORDER_LOC_GEO_FAIL);
    labels.choose_map = t9n(TS.NEW_ORDER_LOC_CHOOSE_MAP);
    labels.prm_addr = t9n(TS.NEW_ORDER_LOC_PRM_ADDR);
    labels.sec_addr = t9n(TS.NEW_ORDER_LOC_SEC_ADDR);
    labels.title_city = t9n(TS.NEW_ORDER_LOC_CITY);
    labels.title_country = t9n(TS.NEW_ORDER_LOC_REGION);
    labels.title_postal = t9n(TS.NEW_ORDER_LOC_POSTAL);
    labels.unlimited_distance = t9n(TS.SETTINGS_LABEL_UNLIMITED_DIST);
    labels.distance = t9n(TS.SETTINGS_LABEL_DISTANCE);
    labels.btn_back = t9n(TS.TXT_BACK);
    labels.btn_save = t9n(TS.CHANGE_PASSWORD_SUBMIT_LABEL);
    labels.btn_next = t9n(TS.TABLE_BUTTON_NEXT);
    labels.btn_prev = t9n(TS.TABLE_BUTTON_PREV);
    labels.invalid_input = t9n(TS.INVALID_INPUT);
    labels.coord = t9n(TS.NEW_ORDER_COORDINATES)
  };

  $: if (!!$session) translate_texts();
</script>

<div class="mdc-layout-grid mobile-margin">
  <div class="map-box">
    <div class="map-wrapper" data-tooltip={t9n(TS.TOOLTIPS_HOW_TO_USE_MAPS)}>
      <div bind:this={autocompleteContainer} class="autocomplete-wrapper" class:invalid={!is_address_valid}></div>

      {#if !address_is_mandatory}
        <div class="coord-search-container">
          <InputWithValidation
            id="coord-search"
            variant="outlined"
            type="text"
            label={labels.coord}
            bind:value={coord_input}
            on:change={handleCoordSearch}
            margin_bottom="0px"
            width="100%"
          />
        </div>
      {/if}

      {#if is_address_valid === false}
        <div class="tooltip-error">
          <span class="tooltip-text tooltip-top">{invalid_text}</span>
        </div>
      {/if}

      <div id="map"></div>
    </div>

    <div class="map-details">
      {#if page === 'settings'}
        {#if $session.user.last_role === 'worker'}
          <div class="mdc-layout-grid" style="padding: 0;">
            <div class="mdc-layout-grid__inner">
              {#if allow_unlimited_distance}
                <div class="mdc-layout-grid__cell" style="margin-top: 15px;">
                  <CheckBox
                    id="virtual-address-checkbox"
                    width="100px"
                    bind:value={input.unlimited_distance}
                    label={labels.unlimited_distance} />
                </div>
              {/if}
              <div class="mdc-layout-grid__cell" style="{allow_unlimited_distance ? 'margin-left: 55px;' : ''}">
                <NumberFieldWithButtons
                  id="duration"
                  type="text"
                  label={labels.distance}
                  bind:value={input.distance}
                  width={allow_unlimited_distance ? "150px" : "310px"}
                  disabled={input.unlimited_distance}
                  is_minmax_val={true}
                  max_val={distance_max}
                  min_val={1} />
              </div>
            </div>
          </div>
        {/if}
      {/if}

      <div class="input-group">
        <InputWithValidation id="address-prim" {variant} label={labels.prm_addr} disabled={true} bind:value={input.primary_address} width='310px' />
        <InputWithValidation id="address-sec" {variant} label={labels.sec_addr} bind:value={input.secondary_address} width='310px' />
        <InputWithValidation id="city" {variant} label={labels.title_city} disabled={true} bind:value={input.city} width='310px' />
        <InputWithValidation id="country" {variant} label={labels.title_country} disabled={true} bind:value={input.region} width='310px' />
        <InputWithValidation id="postal-code" {variant} label={labels.title_postal} disabled={true} bind:value={input.postal_code} width='310px' />
      </div>
    </div>
  </div>

  <div class="actions desktop-next-margin">
    <button onclick="prev_tab()" class="mdc-button mdc-button--outlined margin-5">
      {labels.btn_prev || labels.btn_back}
    </button>

    {#if page === 'settings'}
      <button onclick="update_profile()" class="mdc-button mdc-button--unelevated margin-5" disabled={wait_api}>
        {labels.btn_save}
      </button>
    {:else}
      <button onclick="next_tab()" class="mdc-button mdc-button--unelevated margin-5" disabled={!is_address_valid}>
        {labels.btn_next}
      </button>
    {/if}
  </div>
</div>

<style>
    .coord-search-container { margin-top: 10px; }
    .map-box { display: flex; gap: 15px; }
    .map-wrapper { width: 66%; }
    .map-details { width: 33%; margin-top: 65px; }
    .autocomplete-wrapper { width: 100%; min-height: 40px; }
    .input-group { display: flex; flex-direction: column; gap: 0px; }
    .actions { display: flex; justify-content: flex-end; margin-top: 20px; }

    #map {
        width: 100%;
        height: 285px;
        background: #eee;
        margin-top: 16px;
    }

    .tooltip-error {
        position: relative;
        font-size: 12px;
        top: -42px;
    }

    .tooltip-error .tooltip-text {
        background-color: #b00020;
        color: #fff;
        padding: 8px;
        border-radius: 6px;
    }

    @media (max-width: 1023px) {
        .map-box { flex-direction: column; }
        .map-wrapper, .map-details { width: 100%; }
    }
</style>